import { Accordion, Alert } from 'react-bootstrap';
import {
	useTable,
	useGlobalFilter,
	useFilters,
	useSortBy,
	usePagination,
	TableOptions,
	useBlockLayout,
	Row,
	Cell,
	useRowSelect,
} from 'react-table';
import VehicleFilters from './components/VehicleFilters';
import VehicleTable from './components/VehicleTable';
import VehiclePagination from './components/VehiclePagination';
import TableSkeleton from '../TableSkeleton/TableSkeleton';
import type { TableSkeletonProps } from '../../types/index';
import _ from 'lodash';
import './VehicleTable.scss';
import useTableStore from '../../store/TableStore';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import i18next from 'i18next';
import { VehicleStateEnum } from 'src/api';
import { format } from 'date-fns';

interface TableContainerProps {
	columns: any;
	data: any;
	loading?: any;
	setDownloadData?: any;
	error?: any;
	presetFilter?: any;
	filterIntialState?: any;
	hasFilters?: boolean;
	pageSize?: number;
	hiddenColumns?: string[];
	toggleVisibilityModified?: (id: string) => void;
	handleDoubleClick?: (row: any) => void;
	skeletonProps?: TableSkeletonProps;
	viewLimitMessage?: string;
	EmptyComponent?: React.FC<{ filterState: any }>
	handleStellantisEmptyState?: boolean;
	setMassiveTotalCount?: any;
	setMassiveAllIds?: any;
	setActiveFiltersMassive?: any;
}

const VehicleTableContainer: React.FC<TableContainerProps> = ({
	columns,
	data,
	loading,
	error,
	setDownloadData = null,
	presetFilter = null,
	filterIntialState,
	hasFilters = true,
	pageSize = 10,
	hiddenColumns = [],
	handleDoubleClick = null,
	skeletonProps,
	viewLimitMessage,
	EmptyComponent,
	handleStellantisEmptyState,
	setMassiveTotalCount,
	setMassiveAllIds,
	setActiveFiltersMassive,
	...props
}) => {
	const tableOptions: TableOptions<Object> = {
		columns,
		data,
		initialState: { pageSize, hiddenColumns },
		...(props.toggleVisibilityModified
			? { toggleRow: props.toggleVisibilityModified, autoResetFilters: false }
			: {}),
	};

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		state,
		setAllFilters,
		page,
		rows,
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		toggleAllRowsSelected,
	} = useTable(
		tableOptions,
		useGlobalFilter,
		useFilters,
		useSortBy,
		usePagination,
		useBlockLayout,
		useRowSelect
	);
	const { setPageState, setClearSelectedRowsCallback } = useTableStore();
	const filtersRef = useRef<any>();

	useEffect(() => {
		setClearSelectedRowsCallback(toggleAllRowsSelected);
	}, [setClearSelectedRowsCallback, toggleAllRowsSelected]);

	useEffect(() => {
		setPageState({ state, pageOptions });
	}, [state.pageIndex, state.pageSize, pageOptions.length]);

	// Download table data into csv/xls
	if (!!setDownloadData) {
		if (data?.length) {
			const EXCLUDED_COLS = ['checkboxes', 'id', 'actions', 'display_reporter', 'massive_update_selector'];

			const preparedRows = rows.map((row: Row<Object>) => {
				prepareRow(row);
				if (hiddenColumns.includes('prezzo_listino')) {
					const prezzoCell = row.allCells.find(cell => cell.column.id === 'prezzo_listino');
					prezzoCell && row.cells.push(prezzoCell);
				}
				return row;
			});

			const cells: Cell[][] = preparedRows.map((row: Row<Object>) => row.cells);

			const csvData: any[][] = [];

			if (cells.length) {
				csvData.push([
					...cells[0]
						.filter((cell: Cell) => !EXCLUDED_COLS.includes(cell.column.id))
						.map((cell: Cell) => cell.render('Header')),
					i18next.t('total_optionals_no_iva'),
					i18next.t('opportunity_closing_date'),
				]);
			}
			csvData.push(
				...preparedRows.map((row: Row<Object>) => {
					const original = row.original as any;
					const optionalsSum = original.optionals?.reduce((sum: number, opt: any) => sum + (opt.valore || 0), 0) ?? 0;
					const prezzoOptional = optionalsSum ? parseFloat((optionalsSum / 1.22).toFixed(2)) : '';

					let dataVendita = '';
					if ([VehicleStateEnum.VENDUTO].includes(original.stato_corrente?.type)) {
						try {
							dataVendita = format(original.stato_corrente?.data_inizio || '', 'yyyy-MM-dd');
						} catch (error) {
							console.warn(error);
						}
					}

					return [
						...row.cells
							.filter((cell: Cell) => !EXCLUDED_COLS.includes(cell.column.id))
							.map((cell: Cell) => {
								if (cell.value && typeof cell.value === 'string') {
									cell.value = cell.value.replace(/\n/g, ' ');
								}

								if (cell.column.id === 'offerta_minima') {
									if (cell.value?.valore) {
										return `€${cell.value?.valore} (${cell.value?.noleggiatore})`;
									} else {
										return '';
									}
								} else {
									return cell.value || '';
								}
							}),
						prezzoOptional,
						dataVendita,
					];
				})
			);

			setDownloadData(csvData);
		}
	}

	const showEmptyState = !loading && !error && !rows.length && EmptyComponent;
	const showPagination = rows.length;

	const checkFiltersExpand = useCallback((rows) => {
		const fState = filtersRef.current?.filterState;

		const specificFiltersApplied = [
			fState?.marca?.length > 0,
			fState?.modello?.length > 0,
			fState?.allestimento?.length > 0
		].some(f => f);

		if (!rows.length && filtersRef.current?.isFiltersOpen && specificFiltersApplied) {
			filtersRef.current?.expand(false);
		}
	}, []);

	const debounceFiltersFn = useMemo(
		() => _.debounce((rows) => checkFiltersExpand(rows), 1000),
		[checkFiltersExpand]
	);

	useEffect(() => {
		if (handleStellantisEmptyState) {
			debounceFiltersFn(rows);
		}

		return () => {
			debounceFiltersFn?.cancel();
		};
	}, [state.filters, rows, handleStellantisEmptyState]);

	useEffect(() => {
		setMassiveTotalCount?.(rows.length)
		// @ts-ignore
		setMassiveAllIds?.((rows||[]).map(row => row.original.id))
	}, [rows.length])

	useEffect(() => {
		try {
			const activeFilters = state.filters.filter((filter) => {
				if (Array.isArray(filter.value)) {
					return filter.value.length > 0;
				} else if (typeof filter.value === "object") {
					return Object.values(filter.value).some((val) => val !== "");
				} else {
					return filter.value !== "";
				}
			});
			setActiveFiltersMassive?.(activeFilters)
		} catch (error) {
			console.warn(error);
		}
	}, [state.filters]);

	return (
		<>
			{hasFilters && (
				<Accordion className="mt-4" flush>
					<VehicleFilters
						ref={filtersRef}
						headerGroups={headerGroups}
						setAllFilters={(filters) => {
							toggleAllRowsSelected(false);
							setAllFilters(filters);
						}}
						loading={loading}
						presetFilter={presetFilter}
						filterIntialState={filterIntialState}
					></VehicleFilters>
				</Accordion>
			)}

			{/* {!loading && !error && ( */}
			<>
				{viewLimitMessage ? (
					<Alert variant="warning" className="my-4">
						<div>{viewLimitMessage}</div>
					</Alert>
				) : null}
				{showEmptyState
					? <EmptyComponent filterState={filtersRef.current?.filterState} />
					: <VehicleTable
						getTableProps={getTableProps}
						headerGroups={headerGroups}
						getTableBodyProps={getTableBodyProps}
						page={page}
						prepareRow={prepareRow}
						resultCount={rows.length}
						viewLimitMessage={viewLimitMessage}
						handleDoubleClick={handleDoubleClick}
					></VehicleTable>
				}
				{loading && !error && <TableSkeleton {...skeletonProps} />}

				{Boolean(showPagination) &&
					<VehiclePagination
						gotoPage={gotoPage}
						canPreviousPage={canPreviousPage}
						previousPage={previousPage}
						nextPage={nextPage}
						canNextPage={canNextPage}
						pageCount={pageCount}
						state={state}
						pageOptions={pageOptions}
						setPageSize={setPageSize}
					></VehiclePagination>
				}
			</>

			{/* {(loading && !error) && (
				<TableSkeleton />
			)} */}

			{error && (
				<>
					<div className="table-error-message">{error.message}</div>
				</>
			)}
		</>
	);
};

export default VehicleTableContainer;
